// ROOM 404: Typed HTTP API Service Client

const TOKEN_KEY = 'room404_auth_jwt';

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string | null): void {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

/**
 * Normalizes and resolves an API path against optional VITE_API_BASE_URL.
 * If VITE_API_BASE_URL is absent/empty, preserves relative API path behavior.
 * If path is an absolute URL (e.g. starts with http://, https://, blob:, data:), returns path as-is.
 */
export function resolveApiUrl(path: string, customBaseUrl?: string): string {
  if (!path) return '';
  if (/^(?:https?:)?\/\//i.test(path) || path.startsWith('blob:') || path.startsWith('data:')) {
    return path;
  }

  const rawBase =
    customBaseUrl !== undefined
      ? customBaseUrl
      : (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL
          ? String(import.meta.env.VITE_API_BASE_URL).trim()
          : '');

  if (!rawBase) {
    return path;
  }

  const cleanBase = rawBase.replace(/\/+$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${cleanBase}${cleanPath}`;
}

export async function apiRequest<T = any>(
  path: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: any;
    token?: string;
    isFormData?: boolean;
    headers?: Record<string, string>;
  } = {}
): Promise<T> {
  const token = options.token || getStoredToken();
  const headers = new Headers(options.headers || {});

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (options.body && !options.isFormData && !(options.body instanceof ArrayBuffer)) {
    headers.set('Content-Type', 'application/json');
  }

  let bodyContent = options.body;
  if (options.body && !options.isFormData && !(options.body instanceof ArrayBuffer) && typeof options.body !== 'string') {
    bodyContent = JSON.stringify(options.body);
  }

  const url = resolveApiUrl(path);

  const response = await fetch(url, {
    method: options.method || 'GET',
    headers,
    body: bodyContent,
  });

  const contentType = response.headers.get('Content-Type') || '';
  let data: any;

  if (contentType.includes('application/json')) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const errorMsg = data?.error || data?.message || `HTTP ${response.status}: Request failed`;
    const err: any = new Error(errorMsg);
    err.status = response.status;
    err.data = data;
    throw err;
  }

  return data as T;
}
